import { useState } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, DollarSign, Wallet } from "lucide-react";

interface LoadWalletDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  customerName: string;
  onSuccess?: () => void;
}

export function LoadWalletDialog({
  open,
  onOpenChange,
  customerId,
  customerName,
  onSuccess,
}: LoadWalletDialogProps) {
  const { toast } = useToast();
  const [amountCents, setAmountCents] = useState(10000);
  const [description, setDescription] = useState("");

  const loadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/pay-user", {
        customerId,
        amountCents,
        description,
      });
      if (!response.ok) throw new Error("Payment failed");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer-wallet", customerId] });
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      toast({ title: "Success", description: "Funds loaded successfully" });
      onOpenChange(false);
      setAmountCents(10000);
      setDescription("");
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loadMutation.mutate();
  };

  const { baseCurrency, formatCurrency } = useOrgSettings();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Load Wallet
            </DialogTitle>
            <DialogDescription>
              Add funds to {customerName}'s wallet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Amount Input */}
            <div className="space-y-2">
              <Label htmlFor="amount">{`Amount (${baseCurrency}) *`}</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="amount"
                  type="number"
                  className="pl-9"
                  value={amountCents / 100}
                  onChange={(e) => setAmountCents(Math.round(parseFloat(e.target.value || "0") * 100))}
                  min={0.01}
                  step={0.01}
                  required
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Loading {formatCurrency(amountCents)}
              </p>
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex flex-wrap gap-2">
              {[25, 50, 100, 250, 500].map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant={amountCents === amount * 100 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAmountCents(amount * 100)}
                >
                  {formatCurrency(amount * 100)}
                </Button>
              ))}
            </div>

            {/* Description Input */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Expense reimbursement, bonus, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loadMutation.isPending || amountCents <= 0}>
              {loadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Load {formatCurrency(amountCents)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
